import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { ContentLoading } from '@/components/ContentLoading'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { TabItem } from '@/components/TabBar'
import type { LegendEditorViewModel } from '@/features/legends/LegendEditor/LegendEditorViewModel'
import type { LegendPreviewViewModel } from '@/features/legends/preview/LegendPreviewViewModel'
import type { PreviewInputGroupViewModel } from '@/features/legends/preview/PreviewInputGroupViewModel'
import type { SkillInputsViewModel } from '@/features/legends/preview/SkillInputsViewModel'
import { PreviewComputationsContainer } from '@/features/legends/preview/components/PreviewComputationsContainer'
import {
  PreviewComputeError,
  PreviewInputSection,
  PreviewInputsPane,
  PreviewLayout,
  PreviewOutputsPane,
} from '@/features/legends/preview/components/PreviewLayout'
import {
  PreviewLine,
  PreviewMutedText,
  PreviewOutput,
  PreviewValueText,
} from '@/features/legends/preview/components/PreviewOutput'
import { PreviewQuestionsContainer } from '@/features/legends/preview/components/PreviewQuestions/PreviewQuestionsContainer'
import { SkillToggles } from '@/features/legends/preview/components/SkillToggles'
import { match } from '@/utils/pattern-match-util'

const testId = 'legend-preview'

export interface PreviewTabContainerProps {
  vm: LegendEditorViewModel
}

export const PreviewTabContainer = observer(function PreviewTabContainer({
  vm,
}: PreviewTabContainerProps) {
  const previewVm = vm.previewVm
  return match(previewVm.result, 'type', {
    Loading: () => <ContentLoading />,
    Error: (result) => (
      <PreviewComputeError
        message={result.message}
        onGoToEditor={result.hasLegendErrors ? previewVm.goToEditor : undefined}
        testId={`${testId}-error`}
      />
    ),
    Success: () => (
      <PreviewLayout
        testId={testId}
        inputs={
          <PreviewInputsPane isPending={previewVm.isComputing}>
            <PreviewInputsContainer vm={previewVm} />
          </PreviewInputsPane>
        }
        outputs={
          <PreviewOutputsPane
            tabs={
              <>
                <TabItem
                  active={previewVm.outputTab === 'Computations'}
                  onPress={previewVm.showComputations}
                  testId={`${testId}-computations-tab`}
                >
                  Computations
                </TabItem>
                <TabItem
                  active={previewVm.outputTab === 'Variables'}
                  onPress={previewVm.showVariables}
                  testId={`${testId}-variables-tab`}
                >
                  Variables
                </TabItem>
              </>
            }
          >
            {previewVm.outputTab === 'Computations' ? (
              <PreviewComputationsContainer vm={previewVm} />
            ) : (
              <PreviewVariablesContainer vm={previewVm} />
            )}
          </PreviewOutputsPane>
        }
      />
    ),
  })
})

interface PreviewInputsContainerProps {
  vm: LegendPreviewViewModel
}

const PreviewInputsContainer = observer(function PreviewInputsContainer({
  vm,
}: PreviewInputsContainerProps) {
  return (
    <>
      <InputGroupContainer
        title="Responses"
        vm={vm.questionsVm}
        emptyText="This legend doesn't ask anything yet."
        testId={`${testId}-responses`}
      />
      {vm.hasRoutingContextSources && (
        <InputGroupContainer
          title="Routing context inputs"
          vm={vm.dataSourcesVm}
          testId={`${testId}-routing-context`}
        />
      )}
      {vm.hasCollectedValues && (
        <InputGroupContainer
          title="Organizer collected values"
          vm={vm.organizerVm}
          testId={`${testId}-collected-values`}
        />
      )}
      {vm.skillsVm.availableSkills.length > 0 && (
        <SkillsContainer vm={vm.skillsVm} />
      )}
    </>
  )
})

interface InputGroupContainerProps {
  title: string
  vm: PreviewInputGroupViewModel
  emptyText?: string
  testId?: string
}

const InputGroupContainer = observer(function InputGroupContainer({
  title,
  vm,
  emptyText,
  testId: sectionTestId,
}: InputGroupContainerProps) {
  return (
    <PreviewInputSection
      title={title}
      actions={
        vm.hasResponses && (
          <Button
            label="Clear"
            variant="text"
            size="small"
            onPress={vm.clear}
          />
        )
      }
      testId={sectionTestId}
    >
      <PreviewQuestionsContainer vm={vm} emptyText={emptyText} />
    </PreviewInputSection>
  )
})

interface SkillsContainerProps {
  vm: SkillInputsViewModel
}

const SkillsContainer = observer(function SkillsContainer({
  vm,
}: SkillsContainerProps) {
  return (
    <PreviewInputSection
      title="Skills"
      actions={
        <>
          {!vm.hasAllSkillsSelected && (
            <Button
              label="Select all"
              variant="text"
              size="small"
              onPress={vm.selectAll}
            />
          )}
          {vm.hasSelectedSkills && (
            <Button
              label="Clear"
              variant="text"
              size="small"
              onPress={vm.clear}
            />
          )}
        </>
      }
      testId={`${testId}-skills`}
    >
      <SkillToggles
        skills={vm.skills}
        onToggle={vm.toggleSkill}
        testId={`${testId}-skill`}
      />
    </PreviewInputSection>
  )
})

interface PreviewVariablesContainerProps {
  vm: LegendPreviewViewModel
}

const PreviewVariablesContainer = observer(function PreviewVariablesContainer({
  vm,
}: PreviewVariablesContainerProps) {
  if (vm.variables.length === 0) {
    return (
      <EmptyState size="sm">
        <EmptyStateTitle>No variables</EmptyStateTitle>
        <EmptyStateDescription>
          Add variables in the Questions tab to see their values here.
        </EmptyStateDescription>
      </EmptyState>
    )
  }
  return (
    <>
      {vm.variables.map((variable) => (
        <PreviewOutput
          key={variable.id}
          title={variable.title}
          caption={variable.caption}
          testId={`${testId}-variable-${variable.id}`}
        >
          {!variable.isList ? (
            variable.values.map((value, i) => (
              <PreviewValueText key={i} tone={value.tone} isLarge>
                {value.text}
              </PreviewValueText>
            ))
          ) : variable.values.length === 0 ? (
            <PreviewMutedText>(empty list)</PreviewMutedText>
          ) : (
            variable.values.map((value, i) => (
              <PreviewLine key={i} isCompact>
                <PreviewValueText tone={value.tone}>
                  {value.text}
                </PreviewValueText>
              </PreviewLine>
            ))
          )}
        </PreviewOutput>
      ))}
    </>
  )
})
